import React, { useState, useEffect } from 'react';
import { getMe } from '../api/api';
import { UserOut } from '../api/types';

interface DesktopAccountPageProps {
  onLoginClick: () => void;
}

const DesktopAccountPage: React.FC<DesktopAccountPageProps> = ({ onLoginClick }) => {
  const [user, setUser] = useState<UserOut | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('meine-daten');
  const isLogged = !!localStorage.getItem('access_token');

  useEffect(() => {
    if (isLogged) {
      setLoading(true);
      getMe()
        .then(setUser)
        .catch(console.error)
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
      setUser(null);
    }
  }, [isLogged]);

  if (!isLogged) {
    return (
      <div className="flex flex-col items-center justify-center p-12 bg-white rounded-lg shadow-sm border border-gray-200 w-full h-[400px]">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Mein Konto</h2>
        <p className="text-gray-500 mb-6">Bitte melde dich an, um dein Konto zu verwalten.</p>
        <button onClick={onLoginClick} className="bg-tipico-red text-white px-8 py-3 rounded text-sm font-bold shadow hover:bg-tipico-red-dark transition">Anmelden</button>
      </div>
    );
  }

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    window.dispatchEvent(new Event('auth-changed'));
  };

  const tabs = [
    { id: 'meine-daten', label: 'Meine Daten', icon: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z' },
    { id: 'kontoverifizierung', label: 'Kontoverifizierung', icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z' },
    { id: 'kontoverwaltung', label: 'Kontoverwaltung & Limits', icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
    { id: 'transaktionsverlauf', label: 'Transaktionsverlauf', icon: 'M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4' },
    { id: 'spacer_1', label: '', icon: '', isSpacer: true },
    { id: 'guthaben', label: 'Guthaben', icon: 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z' },
    { id: 'auszahlung', label: 'Auszahlung', icon: 'M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z' },
    { id: 'spacer_2', label: '', icon: '', isSpacer: true },
    { id: 'einstellungen', label: 'Einstellungen', icon: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z' },
    { id: 'neuigkeiten', label: 'Neuigkeiten', icon: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9' },
    { id: 'apps', label: 'Tipico Apps', icon: 'M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z' },
    { id: 'kontakt', label: 'Kontakt', icon: 'M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
    { id: 'spacer_3', label: '', icon: '', isSpacer: true },
    { id: 'abmelden', label: 'Abmelden & Inaktivieren', icon: 'M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1', isLogout: true },
  ];

  return (
    <div className="w-full">
      <div className="flex gap-6 w-full items-start max-w-[1440px] mx-auto px-6">
        {/* Left Sidebar */}
        <div className="w-[280px] shrink-0 flex flex-col">
          {/* Menu Items grouped in white bg sections */}
          <nav className="flex flex-col text-gray-800 font-bold rounded overflow-hidden">
            {tabs.map((tab, idx) => (
              tab.isSpacer ? (
                <div key={`spacer-${idx}`} className="h-4 bg-transparent"></div>
              ) : (
                <button
                  key={tab.id}
                  onClick={() => tab.isLogout ? handleLogout() : setActiveTab(tab.id)}
                  className={`flex items-center gap-4 px-6 py-4 transition text-left bg-white ${
                    activeTab === tab.id
                      ? 'border-l-4 border-tipico-red text-gray-900'
                      : 'border-l-4 border-transparent hover:bg-gray-50'
                  } ${tab.isLogout ? 'text-gray-900' : ''}`}
                >
                  <svg className={`w-[22px] h-[22px] ${activeTab === tab.id || tab.isLogout ? 'text-gray-900' : 'text-gray-600'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={activeTab === tab.id ? 2 : 1.5} d={tab.icon} />
                  </svg>
                  <span className="text-[14px] leading-none">{tab.label}</span>
                </button>
              )
            ))}
          </nav>

          {/* Footer Version */}
          <div className="text-[11px] text-gray-400 mt-6 px-6 pb-4">
            V 3.257.14 m60
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 min-h-[500px] overflow-hidden">
          {loading ? (
            <div className="flex justify-center items-center py-20 bg-white rounded shadow-sm">
              <div className="animate-spin rounded-full h-8 w-8 border-4 border-tipico-red border-t-transparent" />
            </div>
          ) : activeTab === 'meine-daten' ? (
            <div className="bg-white rounded overflow-hidden shadow-sm">
              {/* Internal Tabs */}
              <div className="flex border-b border-gray-200 bg-white">
                <button className="px-6 py-4 text-[14px] font-bold text-gray-900 border-b-[3px] border-tipico-red">
                  Benutzerdaten
                </button>
                <button className="px-6 py-4 text-[14px] font-bold text-gray-500 hover:text-gray-900 transition">
                  Kontaktdaten
                </button>
                <button className="px-6 py-4 text-[14px] font-bold text-gray-500 hover:text-gray-900 transition">
                  Passwort ändern
                </button>
              </div>

              <div className="p-8">
                <div className="grid grid-cols-2 gap-x-12 gap-y-6">
                  <div className="flex flex-col">
                    <label className="text-gray-900 font-bold mb-2 text-[13px]">Vor- und Nachname:</label>
                    <div className="bg-[#f9f9f9] text-gray-500 p-3 rounded text-[14px] border border-gray-200">
                      {user?.username ? `Herr ${user.username}` : 'Herr testeer testbyte'}
                    </div>
                  </div>
                  
                  <div className="flex flex-col">
                    <label className="text-gray-900 font-bold mb-2 text-[13px]">Benutzername:</label>
                    <div className="bg-[#f9f9f9] text-gray-500 p-3 rounded text-[14px] border border-gray-200">
                      {user?.username || 'denemexx123'}
                    </div>
                  </div>
                  
                  <div className="flex flex-col">
                    <label className="text-gray-900 font-bold mb-2 text-[13px]">Geburtsname:</label>
                    <div className="bg-[#f9f9f9] text-gray-500 p-3 rounded text-[14px] border border-gray-200">
                      testbyte
                    </div>
                  </div>
                  
                  <div className="flex flex-col">
                    <label className="text-gray-900 font-bold mb-2 text-[13px]">Geburtsdatum:</label>
                    <div className="bg-[#f9f9f9] text-gray-500 p-3 rounded text-[14px] border border-gray-200">
                      01.01.1980
                    </div>
                  </div>
                  
                  <div className="flex flex-col relative pb-6">
                    <label className="text-gray-900 font-bold mb-2 text-[13px]">E-Mail:</label>
                    <div className="bg-[#f9f9f9] text-gray-500 p-3 rounded text-[14px] border border-gray-200 flex justify-between items-center">
                      <span>svh88299@laoia.com</span>
                      <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="4" d="M5 13l4 4L19 7"></path></svg>
                      </div>
                    </div>
                    <button className="text-[#0066cc] font-medium text-[13px] absolute bottom-0 left-0 hover:underline mt-2 inline-block">
                      E-Mail ändern
                    </button>
                  </div>
                  
                  <div className="flex flex-col">
                    <label className="text-gray-900 font-bold mb-2 text-[13px]">Lugas Spieler ID:</label>
                    <div className="bg-[#f9f9f9] text-gray-500 p-3 rounded text-[14px] border border-gray-200 flex justify-between items-center overflow-hidden">
                      <span className="truncate mr-2">2d4664dd-77db-48ca-a5cb-2b309aad5...</span>
                      <svg className="w-5 h-5 text-gray-400 cursor-pointer hover:text-gray-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </div>
                  </div>
                  
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 text-center max-w-md mx-auto bg-white rounded shadow-sm">
              <svg className="w-16 h-16 text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d={tabs.find(t => t.id === activeTab)?.icon} />
              </svg>
              <h3 className="text-lg font-bold text-gray-900 mb-2">{tabs.find(t => t.id === activeTab)?.label}</h3>
              <p className="text-sm text-gray-500">
                Dieser Bereich befindet sich noch im Aufbau.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DesktopAccountPage;
